<template>
    <div class="magic-disk-page">
        <!-- 固定在顶部的头部区域 -->
        <div class="sticky-header">
            <!-- 页面标题 -->
            <div class="page-header">
                <h1>✨ 网盘资源分享</h1>
                <p>所有资源免费分享，支持分类筛选与关键词搜索</p>
            </div>

            <!-- 分类筛选按钮 -->
            <div class="filter-container">
                <button
                        v-for="cat in categories"
                        :key="cat"
                        :class="['filter-btn', { active: selectedCategory === cat }]"
                        @click="selectedCategory = cat"
                >
                    {{ cat }}
                </button>
            </div>

            <!-- 搜索框 -->
            <div class="search-container">
                <input
                        v-model="searchKey"
                        placeholder="🔍 搜索资源名称、描述或提取码..."
                        @input="handleSearch"
                        class="search-input"
                />
            </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading">
            <p>🌀 加载中...</p>
        </div>

        <!-- 资源卡片列表 -->
        <div class="card-container">
            <!-- 无结果提示 -->
            <div v-if="!loading && filteredResources.length === 0" class="no-result">
                <p v-if="selectedCategory === '全部' && !searchKey">
                    📂 当前没有资源，请检查 data/resources.json 文件。
                </p>
                <p v-else>
                    🔍 未找到 "{{ selectedCategory !== '全部' ? selectedCategory : '' }}{{ searchKey }}" 相关资源
                </p>
            </div>

            <!-- 资源卡片 -->
            <div
                    v-for="(item, index) in filteredResources"
                    :key="index"
                    class="magic-card"
                    @click="handleClick(item)"
            >
                <!-- 分类标签：右上角 -->
                <div class="tag category-tag">{{ item.category }}</div>

                <!-- 图标 -->
                <div class="card-icon">
                    {{ item.name.slice(0, 1).toUpperCase() }}
                </div>

                <!-- 内容区 -->
                <div class="card-content">
                    <h3 v-html="highlight(item.name)"></h3>
                    <p class="desc" v-html="highlight(item.desc)"></p>
                    <div class="link-info">
                        <span class="link-text">🔗 {{ item.link }}</span>
                       <!-- <span class="code">提取码：{{ item.code }}</span>-->
                    </div>
                    <div class="usage">已使用 {{ item.useCount }} 次</div>
                </div>

                <!-- 点击复制按钮：右下角 -->
                <div class="tag copy-badge">点击复制</div>
            </div>
        </div>

        <!-- 底部提示 -->
        <div class="footer-tip">
            <small>💡 点击卡片自动复制【链接 + 提取码】到剪贴板</small>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>📦 网盘资源分享导航</h2>
            <p>精选整理各类实用网盘资源，包括软件工具、学习资料、电子书籍、影视资源等。支持分类筛选、关键词搜索，一键复制链接和提取码。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>分类齐全</strong>：软件、资料、小说、视频等多类别</li>
                <li><strong>智能搜索</strong>：支持关键词快速查找资源</li>
                <li><strong>一键复制</strong>：点击卡片自动复制链接+提取码</li>
                <li><strong>持续更新</strong>：定期补充新资源</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>选择分类筛选或输入关键词搜索</li>
                <li>浏览资源列表，查看详情</li>
                <li>点击资源卡片，自动复制链接和提取码</li>
                <li>粘贴到浏览器地址栏访问网盘</li>
            </ol>

            <h3>💡 资源说明</h3>
            <p>所有资源收集自互联网公开分享，仅供学习交流使用。如有侵权请联系删除。资源有效性会定期检查和更新。</p>

            <h3>📤 投稿资源</h3>
            <p>如果你有优质资源想要分享，欢迎通过网站联系方式提交，审核通过后会添加到资源库。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';

export default {
        name: 'NetDiskShare',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '网盘资源分享 - FreeToolBox',
                seoDescription: '免费分享各类软件、资料、小说、视频等网盘资源，支持分类筛选与关键词搜索。点击卡片一键复制链接和提取码。',
                seoKeywords: '网盘资源分享,百度网盘分享,软件资源,资料分享,免费资源',
                searchKey: '',
                selectedCategory: '全部',
                rawList: [],
                shareList: [],
                loading: true
            }
        },
        computed: {
            categories() {
                const cats = this.shareList.map(item => item.category).filter(Boolean)
                const unique = [...new Set(cats)]
                return ['全部', ...unique]
            },
            filteredResources() {
                let list = this.shareList

                if (this.selectedCategory !== '全部') {
                    list = list.filter(item => item.category === this.selectedCategory)
                }

                const key = this.searchKey.trim().toLowerCase()
                if (!key) return list

                return list.filter(item =>
                    item.name.toLowerCase().includes(key) ||
                    item.desc.toLowerCase().includes(key) ||
                    item.code.toLowerCase().includes(key)
                )
            }
        },
        async mounted() {
            await this.loadResources()
            this.loadUsage()
        },
        methods: {
            async loadResources() {
                try {
                    const res = await fetch('/resources.json?t=' + new Date().getTime())
                    if (!res.ok) throw new Error('资源加载失败')
                    const list = await res.json()

                    this.shareList = list.map(item => ({
                        ...item,
                        useCount: item.useCount || 0
                    }))
                    this.rawList = list
                } catch (err) {
                    console.error('加载资源失败:', err)
                    this.shareList = []
                } finally {
                    this.loading = false
                }
            },

            loadUsage() {
                const saved = localStorage.getItem('diskShareUsage')
                if (saved) {
                    const usage = JSON.parse(saved)
                    this.shareList.forEach(item => {
                        const savedItem = usage.find(s => s.link === item.link)
                        if (savedItem) {
                            item.useCount = savedItem.useCount
                        }
                    })
                }
            },

            saveUsage() {
                const usage = this.shareList.map(({ link, useCount }) => ({ link, useCount }))
                localStorage.setItem('diskShareUsage', JSON.stringify(usage))
            },

            handleSearch() {
                // computed 自动响应
            },

            highlight(text) {
                const key = this.searchKey.trim()
                if (!key) return text

                const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                const regex = new RegExp(`(${escapedKey})`, 'gi')
                return text.toString().replace(regex, '<mark class="highlight">$1</mark>')
            },

            async handleClick(item) {
                const textToCopy = `${item.link}`

                try {
                    await navigator.clipboard.writeText(textToCopy)
                    item.useCount += 1
                    this.saveUsage()
                    this.showToast('✅ 已复制到剪贴板', 'success')
                } catch (err) {
                    this.fallbackCopy(textToCopy)
                    this.showToast('👆 请长按复制', 'warning')
                }
            },

            fallbackCopy(text) {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.focus()
                textarea.select()
                try {
                    document.execCommand('copy')
                } catch (err) {
                    console.error('复制失败', err)
                }
                document.body.removeChild(textarea)
            },

            showToast(msg, type = 'info') {
                const old = document.querySelector('.magic-toast')
                if (old) old.remove()

                const toast = document.createElement('div')
                toast.className = 'magic-toast'
                toast.textContent = msg
                toast.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: ${type === 'success' ? '#52c41a' : type === 'error' ? '#ff4d4f' : '#faad14'};
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transition: opacity 0.3s;
        text-align: center;
        min-width: 200px;
      `
                document.body.appendChild(toast)
                setTimeout(() => {
                    toast.style.opacity = '0'
                    setTimeout(() => toast.remove(), 300)
                }, 2000)
            }
        }
    }
</script>

<style scoped>
    .magic-disk-page {
        min-height: 100vh;
        padding: 20px;
        position: relative;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        color: #333; /* 恢复为深色文字 */
        background: #ffffff; /* ✅ 恢复为纯白色背景 */
    }

    /* 滚动时固定头部 */
    .sticky-header {
        position: sticky;
        top: 0;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
        padding: 16px;
        border-radius: 16px;
        margin-bottom: 24px;
        z-index: 1000;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.1);
    }

    /* 页面标题 */
    .page-header {
        text-align: center;
        margin-bottom: 16px;
    }

    .page-header h1 {
        font-size: 2.2em;
        margin: 0;
        font-weight: 700;
        color: #333;
    }

    .page-header p {
        color: #666;
        margin-top: 6px;
        font-size: 1em;
    }

    /* 分类筛选按钮 */
    .filter-container {
        max-width: 800px;
        margin: 0 auto 16px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .filter-btn {
        padding: 8px 16px;
        border: none;
        background: rgba(0, 0, 0, 0.05);
        color: #555;
        border-radius: 20px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.3s ease;
        min-width: 60px;
        text-align: center;
    }

    .filter-btn:hover {
        background: rgba(0, 0, 0, 0.1);
    }

    .filter-btn.active {
        background: #007bff;
        color: white;
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
    }

    /* 搜索框 */
    .search-container {
        max-width: 600px;
        margin: 0 auto;
    }

    .search-input {
        width: 100%;
        height: 44px;
        border: 1px solid #ddd;
        border-radius: 12px;
        padding: 0 16px;
        font-size: 16px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        outline: none;
        background: white;
        color: #333;
        transition: all 0.3s;
    }

    .search-input::placeholder {
        color: #aaa;
    }

    .search-input:focus {
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    /* 加载状态 */
    .loading {
        text-align: center;
        color: #888;
        padding: 40px 0;
        font-size: 1.1em;
    }

    /* 卡片容器 */
    .card-container {
        max-width: 800px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    /* 无结果提示 */
    .no-result {
        text-align: center;
        color: #888;
        font-size: 1.1em;
        padding: 60px 20px;
        line-height: 1.6;
    }

    /* 魔法卡片 */
    .magic-card {
        background: white;
        border-radius: 16px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        padding: 20px;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        border: 1px solid #eee;
        color: #333;
        backdrop-filter: blur(8px);
        text-align: left;
    }

    .magic-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    }

    /* 通用标签样式 */
    .tag {
        position: absolute;
        font-size: 0.75em;
        font-weight: bold;
        padding: 4px 10px;
        border-radius: 12px;
        opacity: 0.95;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-family: 'Courier New', monospace;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    }

    /* 分类标签 - 右上角 */
    .category-tag {
        top: 12px;
        right: 12px;
        background: #e3f2fd;
        color: #1565c0;
        border: 1px solid #bbdefb;
    }

    /* 点击复制 - 右下角 */
    .copy-badge {
        bottom: 12px;
        right: 12px;
        top: auto;
        background: #f3e5f5;
        color: #7b1fa2;
        border: 1px solid #e1bee7;
    }

    /* 高亮样式 */
    .highlight {
        background-color: #fff3e0;
        color: #e65100;
        padding: 0 4px;
        border-radius: 4px;
        font-weight: bold;
    }

    /* 图标 */
    .card-icon {
        width: 50px;
        height: 50px;
        background: #2196f3;
        border-radius: 12px;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2em;
        font-weight: bold;
        margin-right: 16px;
        flex-shrink: 0;
    }

    /* 内容区 */
    .card-content h3 {
        margin: 0 0 8px 0;
        color: #1a1a1a;
        font-size: 1.3em;
    }

    .desc {
        color: #666;
        font-size: 0.95em;
        margin: 0 0 10px 0;
    }

    .link-info {
        font-size: 0.9em;
        color: #555;
        margin-bottom: 8px;
    }

    .link-text {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 300px;
        color: #1976d2;
    }

    .code {
        color: #d32f2f;
        font-weight: bold;
    }

    .usage {
        font-size: 0.85em;
        color: #777;
    }

    /* 底部提示 */
    .footer-tip {
        text-align: center;
        margin-top: 40px;
        color: #999;
        font-size: 0.9em;
    }

    /* 响应式 */
    @media (max-width: 480px) {
        .magic-card {
            flex-direction: column;
            align-items: flex-start;
            padding: 16px;
        }
        .card-icon {
            margin-bottom: 12px;
            margin-right: 0;
        }
        .link-text {
            max-width: 100%;
        }
        .category-tag, .copy-badge {
            right: 12px;
            font-size: 0.7em;
            padding: 3px 8px;
        }
        .category-tag {
            top: 10px;
        }
        .copy-badge {
            bottom: 10px;
        }
        .filter-btn {
            font-size: 13px;
            padding: 6px 12px;
        }
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #795548;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>